import { Text } from "@overmap-ai/blocks"
import type { Stored, Team } from "@overmap-ai/common"
import {
	type ColumnDef,
	getCoreRowModel,
	getSortedRowModel,
	type TableOptions,
	useReactTable,
} from "@tanstack/react-table"
import { useMemo } from "react"

import { formatTimestampDate } from "../../../utils/date"
import { TeamIcon } from "../../Icons/TeamIcon"
import { ColumnSortButton, ColumnSortIcon } from "../components"

export const useTeamTableColumns = () => {
	return useMemo<ColumnDef<Stored<Team>>[]>(
		() => [
			{
				id: "name",
				accessorKey: "name",
				enableHiding: true,
				meta: {
					plainHeader: "Name",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Name
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: function Cell({ row }) {
					const team = row.original

					return (
						<div className="flex items-center gap-2">
							<TeamIcon teamId={team.uuid} background={false} />
							<Text>{team.name}</Text>
						</div>
					)
				},
			},
			{
				id: "submitted_at",
				accessorFn: (team) => team.submitted_at,
				meta: {
					plainHeader: "Created at",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Created at
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					return <Text accentColor="base">{formatTimestampDate(getValue<string>())}</Text>
				},
			},
		],
		[],
	)
}

export const useTeamTable = (args: Omit<TableOptions<Stored<Team>>, "getCoreRowModel">) => {
	return useReactTable<Stored<Team>>({
		...args,
		getRowId: (originalRow) => originalRow.uuid,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
	})
}
